import { pollUntilReady } from './poll';

/**
 * Источник «файл»: загруженная запись уходит в bit-transcribe-v3, а при
 * разделении участников — в bit-diarize-v3.
 *
 * Контракт источника, общий для всех пяти (файл, ссылка, стерео, встреча,
 * микрофон):
 *
 *   start(payload, deps) → { taskId, baseUrl, kind }
 *   watch(task, deps)    → { stage, progress, eta } … { stage: 'done', result }
 *
 * Зависимости (`post`, `baseUrls`, `onUploadProgress`) приходят снаружи, а не
 * берутся из модуля: так источник проверяется тестом без сети и без
 * переменных окружения, а экран остаётся единственным местом, которое знает
 * про axios и адреса сервисов.
 */

/**
 * Число участников для запроса диаризации: точное ИЛИ вилка.
 *
 * 🔴 Вилку шлём ТОЛЬКО когда точного числа нет: точное число вилку
 * перебивает, так устроен сам конвейер. Отправить оба — значит показать в
 * запросе намерение, которого не будет в результате. Общее у файла и ссылки:
 * ссылка шлёт то же в bit-fetch, а тот — в bit-diarize-v3.
 */
export function speakerParams({ diarize, numSpeakers = null, minSpeakers = null,
                                maxSpeakers = null }) {
  if (!diarize) return {};
  if (numSpeakers !== null) return { num_speakers: numSpeakers };
  const range = {};
  if (minSpeakers !== null) range.min_speakers = minSpeakers;
  if (maxSpeakers !== null) range.max_speakers = maxSpeakers;
  return range;
}

/** Запуск распознавания файла. */
async function start(
  { file, diarize, numSpeakers = null, minSpeakers = null, maxSpeakers = null },
  { post, baseUrls, onUploadProgress },
) {
  const baseUrl = diarize ? baseUrls.diarize : baseUrls.plain;
  // Второй аргумент обязателен: baseUrl бывает относительным путём вида
  // /asr-api (same-origin), и new URL() без базы бросает TypeError на такой
  // строке ещё до первого сетевого запроса. Абсолютный baseUrl эта база не
  // меняет — так устроен сам конструктор URL.
  const url = new URL(`${baseUrl}/start_transcribing`, window.location.origin);
  url.searchParams.append('diarize', diarize);
  Object.entries(speakerParams({ diarize, numSpeakers, minSpeakers, maxSpeakers }))
    .forEach(([name, value]) => url.searchParams.append(name, value));

  // Файл — телом запроса: имя бывает с кириллицей и пробелами, в адресе оно
  // ломает ссылку.
  const body = new FormData();
  body.append('file', file);

  const response = await post(url.toString(), body, { onUploadProgress });
  return { taskId: response.data.task_id, baseUrl, kind: 'file' };
}

/**
 * Ждёт готовности и забирает расшифровку.
 *
 * 🔴 Текст лежит НЕ в ответе get_status — тот отдаёт только состояние и
 * прогресс. За расшифровкой нужен отдельный GET /get_file, и с диаризацией он
 * возвращает список реплик, а без неё — плоский текст строкой. Чтение
 * `segments` из статуса давало пустой экран после успешной задачи.
 */
async function watch(task, deps) {
  const status = await pollUntilReady(task, deps);
  const { data } = await deps.get(`${task.baseUrl}/get_file?task_id=${task.taskId}`);

  const fragments = Array.isArray(data)
    ? namedFragments(data, status.speaker_names || {})
    : parseFlatTranscript(data);

  // Молчащая запись — это «речи не нашли», а не пустой экран без объяснения.
  // status несём дальше: в нём узнавание по голосу, о котором спросит рельс.
  return { fragments, raw: data, status, taskId: task.taskId, noSpeech: fragments.length === 0 };
}

/** Реплики диаризации: узнанный голос подписан именем, остальные — номером. */
function namedFragments(entries, names) {
  return entries.map((entry) => {
    const number = parseInt(String(entry.speaker || '').split('_')[1], 10) || 0;
    return {
      speaker: names[entry.speaker] || `УЧАСТНИК_${number + 1}`,
      // 🔴 Метку диаризации несём дальше показанного имени: «запомнить голос»
      // отправляет именно её — векторы в bit-history лежат под SPEAKER_N, а на
      // экране стоит имя. Без неё Протокол не может привязать голос к участнику.
      speakerId: entry.speaker || null,
      start: entry.start,
      end: entry.end,
      text: String(entry.text || '').trim(),
    };
  });
}

/** Плоская расшифровка: строка на реплику, таймкод в квадратных скобках.
 *
 * Таймкод — до секунд: миллисекунды сервера (`[00:00:00.320 - …]` у parakeet)
 * человеку не нужны и в Историю не уходят. Тот же разбор у ссылки (link.js).
 *
 * 🔴 Двоеточие ПОСЛЕ скобки обязано сниматься здесь. Сервер пишет строку как
 * `[00:00:00.000 - 00:00:18.593]: текст` (bit-transcribe-v3/app/storage.py:172),
 * а разбор съедал только скобку — и двоеточие уезжало в начало реплики. На
 * экране каждая фраза начиналась с «: », как будто у неё отняли имя говорящего.
 * Найдено владельцем 05.09.2026 на обычной транскрибации без диаризации.
 */
function parseFlatTranscript(text) {
  return String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = line.match(/^\[(\d{2}:\d{2}:\d{2})[.\d]* - (\d{2}:\d{2}:\d{2})[.\d]*\]\s*:?\s*(.*)$/);
      // Конец отрезка экран не показывает, но он нужен субтитрам
      // (lib/transcriptExport.js): без него конец пришлось бы угадывать.
      return match
        ? { speaker: null, timecode: match[1], endTimecode: match[2], text: match[3] }
        : { speaker: null, timecode: null, text: line };
    });
}

/**
 * Снятие задачи на сервере.
 *
 * 🔴 Именно на сервере. Кнопка «Отменить» до 01.09 только чистила экран:
 * видеокарта продолжала считать, минуты списывались с квоты, а запись потом
 * тихо появлялась в Истории — человек был уверен, что остановил работу.
 */
async function cancel({ taskId, baseUrl }, { cancelTask }) {
  return cancelTask(baseUrl, taskId);
}

export const fileSource = { key: 'file', start, watch, cancel };
